import React, { useEffect } from 'react';

const AdapterPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Adapter Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Adapter Pattern - allow incompatible interfaces to work together. Perfect for third-party integration and legacy system compatibility.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Adapter Pattern - allow incompatible interfaces to work together. Perfect for third-party integration and legacy system compatibility.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'adapter pattern, design patterns, structural patterns, interface compatibility, third-party integration, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'adapter pattern, design patterns, structural patterns, interface compatibility, third-party integration, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Adapter Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Make incompatible interfaces work together
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Adapter pattern allows incompatible interfaces to work together. It acts as a bridge 
            between two incompatible interfaces by wrapping an existing class with a new interface.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You have an existing class that you want to use, but its interface doesn't match what 
            you need. Or you're integrating with a third-party library that has a different interface 
            than what your application expects.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Payment Processing</h2>
          <div style={styles.codeBlock}>
{`// Target interface (what client expects)
interface PaymentProcessor {
    void processPayment(double amount, String currency);
    boolean validatePayment(String paymentDetails);
}

// Existing third-party payment services (Adaptees)
class StripePaymentService {
    public void makePayment(double amount, String curr, String cardToken) {
        System.out.println("Processing $" + amount + " " + curr + " via Stripe");
        System.out.println("Card token: " + cardToken);
    }
    
    public boolean isValidCard(String cardToken) {
        return cardToken != null && cardToken.length() > 10;
    }
}

class PayPalPaymentService {
    public void sendPayment(double amt, String currency, String email) {
        System.out.println("Sending $" + amt + " " + currency + " via PayPal");
        System.out.println("PayPal email: " + email);
    }
    
    public boolean verifyAccount(String email) {
        return email != null && email.contains("@");
    }
}

// Adapters
class StripeAdapter implements PaymentProcessor {
    private StripePaymentService stripeService;
    
    public StripeAdapter(StripePaymentService stripeService) {
        this.stripeService = stripeService;
    }
    
    @Override
    public void processPayment(double amount, String currency) {
        // Adapt the interface - assume we have card token from context
        String cardToken = "stripe_token_" + System.currentTimeMillis();
        stripeService.makePayment(amount, currency, cardToken);
    }
    
    @Override
    public boolean validatePayment(String paymentDetails) {
        return stripeService.isValidCard(paymentDetails);
    }
}

class PayPalAdapter implements PaymentProcessor {
    private PayPalPaymentService paypalService;
    
    public PayPalAdapter(PayPalPaymentService paypalService) {
        this.paypalService = paypalService;
    }
    
    @Override
    public void processPayment(double amount, String currency) {
        // Adapt the interface - assume we have email from context
        String email = "user@example.com";
        paypalService.sendPayment(amount, currency, email);
    }
    
    @Override
    public boolean validatePayment(String paymentDetails) {
        return paypalService.verifyAccount(paymentDetails);
    }
}

// Client code
class PaymentService {
    private PaymentProcessor processor;
    
    public PaymentService(PaymentProcessor processor) {
        this.processor = processor;
    }
    
    public void processOrder(double amount, String currency, String paymentDetails) {
        if (processor.validatePayment(paymentDetails)) {
            processor.processPayment(amount, currency);
            System.out.println("Payment processed successfully!");
        } else {
            System.out.println("Invalid payment details!");
        }
    }
}

// Usage
public class AdapterDemo {
    public static void main(String[] args) {
        // Using different payment services through adapters
        PaymentService paymentService;
        
        // Stripe
        StripeAdapter stripeAdapter = new StripeAdapter(new StripePaymentService());
        paymentService = new PaymentService(stripeAdapter);
        paymentService.processOrder(100.0, "USD", "stripe_token_12345");
        
        // PayPal
        PayPalAdapter paypalAdapter = new PayPalAdapter(new PayPalPaymentService());
        paymentService = new PaymentService(paypalAdapter);
        paymentService.processOrder(75.0, "EUR", "user@paypal.com");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Reusability:</strong> Allows reuse of existing classes</li>
            <li style={styles.listItem}><strong>Separation of Concerns:</strong> Separates interface conversion from business logic</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Can add new adapters without modifying existing code</li>
            <li style={styles.listItem}><strong>Integration:</strong> Enables integration with third-party libraries</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you want to use an existing class with incompatible interface</li>
            <li style={styles.listItem}>When integrating with third-party libraries</li>
            <li style={styles.listItem}>When you need to make legacy code work with new systems</li>
            <li style={styles.listItem}>When you want to create a reusable class that cooperates with classes with incompatible interfaces</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Adapter pattern is essential for integrating incompatible systems and making legacy 
            code work with modern applications. It provides a clean way to bridge different interfaces 
            without modifying existing code.
          </p>
        </section>
      </main>
    </article>
  );
};

export default AdapterPattern;